import { QueryClient } from '@tanstack/react-query'
import type { Product } from '@skop-app/lib-domain/types'

import { adaptCatalogProductStatuses } from '@/features/property/legacy/catalogEditor.productAdapter'
import type { ProductStatusesQueryData } from '@skop-app/lib-domain/types'
import type { Context } from '@/types'
import { getReducedProducts } from '@/utils'

const isCatalogEligibleOccurrence = (occurrence: Partial<Product>) =>
  occurrence.progress !== 'done' &&
  occurrence.type !== 'done' &&
  occurrence.nature !== 'service'

// Read adapted products for a given projectID from React Query cache
export const getAdaptedProductsFromCache = (
  queryClient: QueryClient,
  projectID: string,
  context: Context,
) => {
  const all = queryClient.getQueriesData<{
    pages?: Array<{ items?: Product[]; nextToken?: string | null }>
  }>({ queryKey: ['productStatuses'] })

  // Keep only queries matching the projectID
  const pages = all
    .filter(([queryKey]) => {
      try {
        const scope = Array.isArray(queryKey) ? queryKey[1] : null
        if (!scope || typeof scope !== 'object') return false
        const filters =
          'filters' in scope
            ? (scope as { filters?: { projectID?: { eq?: string } } }).filters
            : undefined
        return filters?.projectID?.eq === projectID
      } catch {
        return false
      }
    })
    .flatMap(([, data]) => data?.pages ?? [])

  const items = pages.flatMap((page) => page?.items ?? [])

  // Deduplicate per status id by newest updatedAt
  const byId = new Map<string, Product>()
  for (const item of items) {
    if (!item?.id) continue
    const existing = byId.get(item.id)
    if (!existing) {
      byId.set(item.id, item)
    } else {
      const a = new Date(existing.updatedAt || 0).getTime()
      const b = new Date(item.updatedAt || 0).getTime()
      if (b >= a) byId.set(item.id, item)
    }
  }

  const dedupbed = Array.from(byId.values())
  return adaptCatalogProductStatuses(dedupbed, context)
}

export const reconcileProductOccurrencesInCache = (
  queryClient: QueryClient,
  productID: string,
  occurrences: Product[] | undefined,
) => {
  const normalizedOccurrences = Array.isArray(occurrences) ? occurrences : []

  queryClient.setQueryData(['product', productID], normalizedOccurrences)

  if (!normalizedOccurrences.length) return

  const incomingById = new Map(
    normalizedOccurrences
      .filter(
        (occurrence): occurrence is Product & { id: string } =>
          typeof occurrence?.id === 'string',
      )
      .map((occurrence) => [occurrence.id, occurrence]),
  )

  if (!incomingById.size) return

  const cachedQueries = queryClient.getQueriesData<ProductStatusesQueryData>({
    queryKey: ['productStatuses'],
  })

  cachedQueries.forEach(([queryKey, data]) => {
    if (!data?.pages?.length) return

    let hasChanged = false
    const pages = data.pages.map((page) => {
      const items = (page.items ?? []).map((item) => {
        const incoming = incomingById.get(item.id)
        if (!incoming) return item
        hasChanged = true
        return { ...item, ...incoming }
      })

      return {
        ...page,
        items,
      }
    })

    if (!hasChanged) return

    queryClient.setQueryData(queryKey, {
      ...data,
      pages,
    })
  })
}

export const getReducedProductFromProductQueryCache = (
  queryClient: QueryClient,
  context: Context,
  productID: string,
  projectID: string,
) => {
  const occurrences =
    queryClient.getQueryData<Product[]>(['product', productID]) ?? []
  if (!occurrences.length) return undefined

  const eligibleOccurrences = occurrences.filter(
    (occurrence) =>
      occurrence.projectID === projectID &&
      isCatalogEligibleOccurrence(occurrence),
  )
  if (!eligibleOccurrences.length) return undefined

  const adapted = adaptCatalogProductStatuses(eligibleOccurrences, context)
  const reduced = getReducedProducts(adapted)
  return reduced.find((product) => product.productID === productID)
}

// Convenience: get the reduced (parent-level) product for a given productID
export const getReducedProductFromCache = (
  queryClient: QueryClient,
  projectID: string,
  context: Context,
  productID: string,
) => {
  const fromProductQuery = getReducedProductFromProductQueryCache(
    queryClient,
    context,
    productID,
    projectID,
  )
  if (fromProductQuery) return fromProductQuery

  const adapted = getAdaptedProductsFromCache(queryClient, projectID, context)
  const reduced = getReducedProducts(adapted)
  const product = reduced.find((p) => p.productID === productID)
  return product
}
